import { switchMap, take } from 'rxjs/operators';
import { MultiPointInstallation } from '../../types/api';
import {
  MultiPointInstallationEditAction,
  MultiPointInstallationEditActionTypes,
  MultiPointInstallationEditState,
} from '../../types/multi-point-installation-edit';
import {
  ResourceStatus,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
} from '../../types/resource';
import { OrganizationIdOrAll } from '../../types/all-organizations';
import { actions } from '../index';
import { getRequiredSessionToken } from '../../utils/session-token';

import createRxStore, { rxDispatch, ofType } from '..';

import { SenseiApi } from '../../utils/api';
import { Toaster } from '@densityco/ui';

export const initialState: MultiPointInstallationEditState = {
  resource: RESOURCE_IDLE,
  saveButtonDisabled: false,
};

function reducer(
  state: MultiPointInstallationEditState,
  action: MultiPointInstallationEditAction,
): MultiPointInstallationEditState {
  switch (action.type) {
    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_CHANGE_PAGE:
      return { ...state, resource: RESOURCE_IDLE };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_LOADING:
      return { ...state, resource: RESOURCE_LOADING };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            id: action.item.id,
            name: action.item.name,
            height: `${action.item.sensor_height}`,
            sensors: action.item.sensors.map((sensor, index) => ({
              serialNumber: sensor.sensor_serial,
              spacingRight:
                index < action.item.sensors.length - 1
                  ? `${sensor.spacing_right}`
                  : null,
              doorwayName: sensor.doorway_name,
            })),
            serialNumberPositions: action.item.sensors.map(
              (i) => i.sensor_serial,
            ),

            config: action.item.config,
            maxXOffsetMultiplier: action.item.config['max_x_offset_multiplier']
              ? `${action.item.config['max_x_offset_multiplier']}`
              : '',
            spanMS: action.item.config['span_ms']
              ? `${action.item.config['span_ms']}`
              : '',
          },
        },
        saveButtonDisabled: false,
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_NAME:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            name: action.name,
          },
        },
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_HEIGHT:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            height: action.height,
          },
        },
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_SPAN_MS:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            spanMS: action.spanMS,
          },
        },
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_MAX_X_OFFSET_MULTIPLIER:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            maxXOffsetMultiplier: action.maxXOffsetMultiplier,
          },
        },
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
        saveButtonDisabled: false,
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_SPACING_RIGHT:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            sensors: state.resource.data.sensors.map((s) =>
              s.serialNumber === action.serialNumber
                ? { ...s, spacingRight: action.spacingRight }
                : s,
            ),
          },
        },
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_REMOVE_SENSOR: {
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      const sensors = state.resource.data.sensors.filter(
        (s) => s.serialNumber !== action.serialNumber,
      );

      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            sensors,
            serialNumberPositions: sensors.map((i) => i.serialNumber),
          },
        },
      };
    }

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_ADD_SENSORS: {
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      const sensors = [
        ...state.resource.data.sensors,

        // Add all the new values
        ...action.sensors.map((s) => ({
          ...s,
          spacingRight: '',
        })),
      ].map((p) => ({
        // Add empty spacingRight values where necessary.
        ...p,
        spacingRight: p.spacingRight !== null ? p.spacingRight : '',
      }));

      // And, ensure that the final position doesn't have a spacingRight value.
      sensors[sensors.length - 1].spacingRight = null;

      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            sensors,
            serialNumberPositions: sensors.map((i) => i.serialNumber),
          },
        },
      };
    }

    // Used by the reordering modal to "commit" this new order to the main page
    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_SENSOR_ORDER:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }

      const sensors = state.resource.data.sensors;

      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            sensors: action.serialNumbers
              .map((sn) => sensors.find((s) => s.serialNumber === sn))
              .map((s, index) => ({
                ...s,
                spacingRight: sensors[index].spacingRight,
              })),
          },
        },
      };

    case MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SUBMIT:
      return { ...state, saveButtonDisabled: true };

    default:
      return state;
  }
}

const MultiPointInstallationEditStore = createRxStore(
  'MultiPointInstallationEditStore',
  initialState,
  reducer,
);

async function loadMPI(
  dispatch,
  jwtToken: string,
  mpiId: MultiPointInstallation['id'],
) {
  dispatch({
    type: MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_LOADING,
  });

  let mpi: any;
  try {
    mpi = await SenseiApi.multiPointInstallationDetail(jwtToken, mpiId);
  } catch (error) {
    dispatch({
      type: MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_LOAD_COMPLETE,
    item: mpi,
  });
}

async function updateMPI(
  dispatch,
  jwtToken: string,
  organizationIdOrAll: OrganizationIdOrAll,
  mpiId: MultiPointInstallation['id'],
  state: MultiPointInstallationEditState,
) {
  if (state.resource.status !== ResourceStatus.COMPLETE) {
    return;
  }
  try {
    await SenseiApi.multiPointInstallationUpdate(jwtToken, mpiId, {
      name: state.resource.data.name,
      sensor_height: parseFloat(state.resource.data.height),
      sensors: state.resource.data.sensors.map((p) => ({
        sensor_serial: p.serialNumber,
        spacing_right:
          p.spacingRight !== null
            ? parseFloat(p.spacingRight)
            : undefined /* omit the key when json serialized */,
      })),
      config: {
        ...state.resource.data.config,
        span_ms:
          state.resource.data.spanMS.length > 0
            ? parseFloat(state.resource.data.spanMS)
            : undefined,
        max_x_offset_multiplier:
          state.resource.data.maxXOffsetMultiplier.length > 0
            ? parseFloat(state.resource.data.maxXOffsetMultiplier)
            : undefined,
      },
    });
  } catch (error) {
    dispatch({
      type: MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_ERROR,
      error,
    });
    return;
  }

  // Go back to previous page from before the edit page was loaded when successful.
  window.history.back();

  // Show a toast to provide feedback that saving was successful.
  setTimeout(() => {
    Toaster.showToast({ text: 'Saved MPI.' });
  }, 500);
}

// =================================
// SIDE EFFECT: on initial load of the list view, figure out what request needs to be made and make it to
// load the data for the right view.
// =================================
actions
  .pipe(
    ofType<{ id: MultiPointInstallation['id'] }>(
      MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_CHANGE_PAGE,
    ),
    switchMap(
      () => MultiPointInstallationEditStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    loadMPI(rxDispatch, jwtToken, action.id);
  });

// =================================
// SIDE EFFECT: when the "save" button is clicked, send an update to the server.
// =================================
actions
  .pipe(
    ofType<{ organizationIdOrAll: OrganizationIdOrAll }>(
      MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SUBMIT,
    ),
    switchMap(
      () => MultiPointInstallationEditStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    updateMPI(
      rxDispatch,
      jwtToken,
      action.organizationIdOrAll,
      state.resource.data.id,
      state,
    );
  });

// =================================
// SIDE EFFECT: delete the MPI, request reload of MPIs
// =================================
actions
  .pipe(
    ofType<{ organizationIdOrAll: OrganizationIdOrAll }>(
      MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_DELETE,
    ),
    switchMap(
      () => MultiPointInstallationEditStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    if (state.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }

    return SenseiApi.multiPointInstallationDelete(
      jwtToken,
      state.resource.data.id,
    )
      .then(() => {
        Toaster.showToast({ text: 'Deleted MPI.' });
        window.location.href = `#/${action.organizationIdOrAll}/multi-point-installations`;
      })
      .catch(() => {
        Toaster.showToast({ text: 'Error deleting MPI!', type: 'error' });
      });
  });

export default MultiPointInstallationEditStore;
